/**
 * Checkin events — the check-in landing list: a scan-only actor's fallback
 * destination (`defaultAdminRoute`'s `tickets.checkin.scan` branch), listing
 * every event exactly as the admin grid does — `events.state.ts`'s own
 * `eventsPageView`/`eventsListState`, public read/list (`Event`'s own ICAN
 * rule is `and()` for both), same schedule order. `event-tile.view.ts` is the
 * shared poster card both this list and `event-list.view.ts`'s admin grid
 * render, so a scan-only actor sees the identical tile an owner sees — no
 * sold/capacity footer here (that stat rides `tickets.reports.view`, a
 * permission this actor may lack, and its reconciler is never mounted
 * alongside this route) and no create affordance (`event-tile` carries
 * neither on its own).
 *
 * A tile click re-dispatches as `open-event` — the same cancelable
 * bubbling/composed shape `event-list.view.ts` re-dispatches its own tile
 * clicks as — so the routing host (`checkin-events-view`, web-side) wires it
 * exactly like `events.view.ts`'s own `@open-event` handler.
 */
import { fill, overflowWithin } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, type TemplateResult } from "lit";
import { choose } from "lit/directives/choose.js";
import { repeat } from "lit/directives/repeat.js";
import "../../events/event-tile.view.ts";
import { eventsListState, eventsPageView } from "../../events/events.state.ts";
import { LocalizeMixin } from "../../i18n/localize.ts";

@schmancyElement("checkin-events")
export class CheckinEventsView extends LocalizeMixin(SchmancyElement) {
	private open(id: string): void {
		this.dispatchEvent(
			new CustomEvent("open-event", {
				detail: { id },
				bubbles: true,
				composed: true,
				cancelable: true,
			}),
		);
	}

	override render(): TemplateResult {
		const { rows } = eventsPageView.get();

		// "error"/"denied" render the same frame — eventsListState's own
		// "denied" branch can't fire for Event (public read/list), but choose()
		// still needs a case to satisfy the union.
		const troubleFrame = () => html`
			<div class="flex flex-col items-start gap-3 py-8">
				<schmancy-icon class="text-3xl text-error-default">error</schmancy-icon>
				<schmancy-typography type="title" token="sm">${this.t("admin.events.list.errorTitle")}</schmancy-typography>
				<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
					${this.t("admin.events.list.errorBody")}
				</schmancy-typography>
			</div>
		`;

		return html`
			<section ${fill()} class="flex flex-col gap-3 p-4 md:p-6">
				<schmancy-typography type="title" token="md">
					${this.t("scanner.session.pickEvent")}
				</schmancy-typography>

				<div ${overflowWithin()} class="min-h-0">
					${choose(eventsListState.get(), [
						[
							"loading",
							() => html`
								<div
									role="status"
									aria-busy="true"
									aria-label=${this.t("admin.events.list.loadingAriaLabel")}
									class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
								>
									${repeat(
										[0, 1, 2, 3, 4] as const,
										(i) => i,
										() => html`
											<div class="flex flex-col gap-2">
												<schmancy-skeleton class="aspect-[2/3] w-full rounded-2xl"></schmancy-skeleton>
												<schmancy-skeleton shape="text" class="w-3/4"></schmancy-skeleton>
											</div>
										`,
									)}
								</div>
							`,
						],
						["error", troubleFrame],
						["denied", troubleFrame],
						[
							"empty",
							() => html`
								<div class="flex flex-col items-start gap-3 py-8">
									<schmancy-icon class="text-3xl text-surface-onVariant">event</schmancy-icon>
									<schmancy-typography type="title" token="sm">${this.t("admin.events.list.emptyTitle")}</schmancy-typography>
									<schmancy-typography type="body" token="sm" class="text-surface-onVariant max-w-xs">
										${this.t("admin.checkin.list.emptyBody")}
									</schmancy-typography>
								</div>
							`,
						],
						[
							"populated",
							() => html`
								<div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
									${repeat(
										rows,
										(r) => r.id,
										(r) => html`
											<event-tile
												.row=${r}
												@open=${(e: CustomEvent<{ id: string }>) => this.open(e.detail.id)}
											></event-tile>
										`,
									)}
								</div>
							`,
						],
					])}
				</div>
			</section>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"checkin-events": CheckinEventsView;
	}
}
